/* eslint-disable react/no-unstable-nested-components */
import {
  AttributeEditor,
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Input,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import {
  DefaultTolerance,
  Materials,
  PartTypes,
  PPartStatus,
  PPartStatusMap,
  StepTypes,
} from '@parallel-fluidics/shared/constants';
import {
  getPPartSchemaByType,
  InstancedPartTypes,
  PistonFlashGapOptions,
  PistonSizeOptions,
  PPartMetadataKeyOptions,
  pPartToFirestore,
} from '@parallel-fluidics/shared/models/projects/parts';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import PartRefEditor from 'features/pPart/components/PartRefEditor';
import {
  collection,
  doc,
  getFirestore,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'sonner';
import { incrementString } from 'utils/format';
import * as Yup from 'yup';

const stepOptions = Object.values(StepTypes).map((value) => ({ label: value, value }));
export default function AddPartModal({
  open,
  onClose,
  onSuccess = () => {},
  pParts,
  projectDocId,
  isInternal,
  isUsed = false,
  isEditing = false,
  initPart = {},
}) {
  const { user } = useUserData();
  const queryClient = useQueryClient();
  const initialValues = useMemo(() => ({
    partId: '',
    revision: 'A',
    name: '',
    material: '',
    type: '',
    instance: 1,
    cavities: [],
    size: '',
    flashGap: '',
    notes: '',
    uid: user.id,
    metadataArr: [],
    status: PPartStatus.ACTIVE,
    linkedParts: [],
    components: [],
    ...initPart,
    materialArr: initPart?.material ? [{ label: initPart.material, value: initPart.material }] : [],
  }), [initPart, user.id]);

  const [filteringText, setFilteringText] = useState('');
  const [materialOptions, setMaterialOptions] = useState(
    Object.values(Materials).map((material) => ({ label: material, value: material })) || [],
  );
  const [debouncedPartId, setDebouncedPartId] = useState(null);

  // pParts is real-time from Firestore, and since we don't have pagination,
  // it's fine to filter on the client side
  const partsWithSamePartId = useMemo(() => {
    if (!debouncedPartId || !pParts?.length) return [];
    return pParts.filter((p) => p.partId === debouncedPartId)
      .sort((a, b) => b.revision.charCodeAt(0) - a.revision.charCodeAt(0));
  }, [pParts, debouncedPartId]);

  // find parts with same partId but different id (exclude itself when editing)
  const foundParts = useMemo(() => {
    if (!partsWithSamePartId) return [];
    return partsWithSamePartId?.filter((p) => p.id !== initialValues.id) || [];
  }, [partsWithSamePartId, initialValues?.id]);

  const {
    values, errors, handleSubmit, setFieldValue, isSubmitting,
  } = useFormik({
    initialValues,
    validateOnBlur: false,
    validationSchema: Yup.lazy((validateValues) => getPPartSchemaByType(validateValues.type)),
    validate: (validateValues) => {
      const err = {};
      const samePartAndRevision = (p) => p.partId === validateValues.partId
        && p.revision === validateValues.revision;
      if (InstancedPartTypes.includes(validateValues.type)) {
        // A new instanced part (tool, piston) can't collide: its instance is
        // assigned automatically (and checking it here would race the
        // instance effect). An edited one keeps its number while its
        // revision stays editable, so no other part may already hold the
        // same partId + revision + instance.
        if (isEditing && foundParts?.some((p) => samePartAndRevision(p)
          && p.instance === validateValues.instance)) {
          err.partId = 'Found duplicated part with same partId, revision and instance';
        }
      } else if (foundParts?.some((p) => samePartAndRevision(p)
        && validateValues.materialArr?.some((m) => m.value === p.material))) {
        err.partId = 'Found duplicated part with same partId, revision and material';
      }
      return err;
    },
    onSubmit: async (submitValues) => {
      try {
        if (submitValues.type === PartTypes.PRODUCT && submitValues.materialArr?.length > 0) {
          // create/edit multiple 'Product' parts with different materials
          const { materialArr, ...restSubmitValues } = submitValues;
          await Promise.all(materialArr.map(async (materialOption) => {
            const material = materialOption.value;
            const docData = pPartToFirestore({ ...restSubmitValues, material }, serverTimestamp);
            const pPartRef = (initPart?.id && initPart.material === material)
              ? doc(getFirestore(), 'projects', projectDocId, 'parts', initPart.id)
              : doc(collection(getFirestore(), 'projects', projectDocId, 'parts'));
            await setDoc(pPartRef, docData);
          }));
        } else {
          const docData = pPartToFirestore(submitValues, serverTimestamp);
          const pPartRef = initPart?.id
            ? doc(getFirestore(), 'projects', projectDocId, 'parts', initPart.id)
            : doc(collection(getFirestore(), 'projects', projectDocId, 'parts'));
          await setDoc(pPartRef, docData);
        }
        // The parts list itself is realtime, but PPartDisplay and the part
        // pickers read through React Query — refresh them, or notes/BOM
        // cells keep the pre-edit doc for the whole staleTime. The list
        // prefix also covers created docs (incl. new multi-material
        // siblings) and the pPartId-based PPartDisplay lookups.
        if (initPart?.id) {
          queryClient.invalidateQueries({
            queryKey: ['getProjectPartByDocId', projectDocId, initPart.id],
          });
        }
        queryClient.invalidateQueries({ queryKey: ['getProjectParts', projectDocId] });
        onSuccess();
        onClose();
      } catch (error) {
        Sentry.captureException(error, { extra: { projectDocId, part: submitValues } });
        toast.error(`Error ${isEditing ? 'editing' : 'creating'} part: ${error.message}`);
      }
    },
  });

  // load next part id for creating new part
  useEffect(() => {
    if (isEditing) return;
    // sort by partId desc to get the latest part
    const sorted = [...pParts].sort((a, b) => b.partId.localeCompare(a.partId));
    const firstPart = sorted[0];
    // if no parts, start from default '0000'
    const defaultProjectId = '0000';
    const partId = incrementString(firstPart?.partId || defaultProjectId);
    setFieldValue('partId', partId);
  }, [isEditing, pParts, setFieldValue]);

  useEffect(() => {
    if (!foundParts?.length) return undefined;
    const latestPart = foundParts[0];
    // if editing the current part, do not auto update
    if (isEditing && latestPart.partId === initialValues.partId) return undefined;
    // auto update based on the part with the same partId
    setFieldValue('revision', String.fromCharCode(latestPart.revision.charCodeAt(0) + 1));
    setFieldValue('name', latestPart.name);
    setFieldValue('type', latestPart.type);
    if (latestPart.stepType) setFieldValue('stepType', latestPart.stepType);
    if (latestPart.material) {
      setFieldValue('material', latestPart.material);
      setFieldValue('materialArr', [{ label: latestPart.material, value: latestPart.material }]);
    }
    if (latestPart.cavities?.length > 0) setFieldValue('cavities', latestPart.cavities);
    if (latestPart.size) setFieldValue('size', latestPart.size);
    if (latestPart.flashGap) setFieldValue('flashGap', latestPart.flashGap);
    if (latestPart.notes) setFieldValue('notes', latestPart.notes);
    if (latestPart.linkedParts?.length > 0) {
      setFieldValue('linkedParts', latestPart.linkedParts);
    }
    if (latestPart.components?.length > 0) {
      setFieldValue('components', latestPart.components);
    }
    return () => {
      setFieldValue('revision', 'A');
      setFieldValue('name', '');
      setFieldValue('material', '');
      setFieldValue('materialArr', []);
      setFieldValue('type', '');
      setFieldValue('stepType', '');
      setFieldValue('instance', 1);
      setFieldValue('size', '');
      setFieldValue('flashGap', '');
      setFieldValue('notes', '');
      setFieldValue('linkedParts', []);
      setFieldValue('components', []);
    };
  }, [foundParts, setFieldValue, isEditing, initialValues?.partId]);

  // An existing part with the same partId + revision: a new instanced part
  // (tool, piston) is then another copy of that design and shares its config.
  const sameRevisionPart = useMemo(() => (
    foundParts?.find((p) => p.partId === values.partId && p.revision === values.revision) || null
  ), [foundParts, values.partId, values.revision]);

  // Auto set instance for instanced types (tools, pistons): next number
  // for the same partId + revision, else 1. Not while editing: an existing
  // instance keeps its number (and so its pPartId) — foundParts excludes
  // the part itself, so renumbering would bump it past its siblings.
  // Pistons always take a same-revision sibling's size / flash gap (which
  // the form then keeps read-only), including when an edit moves a piston
  // onto an existing revision, so instances of a design never diverge.
  useEffect(() => {
    if (!InstancedPartTypes.includes(values.type) || !foundParts?.length) return;
    if (!isEditing) {
      setFieldValue('instance', sameRevisionPart ? (sameRevisionPart.instance || 0) + 1 : 1);
    }
    if (sameRevisionPart && values.type === PartTypes.PISTON) {
      setFieldValue('size', sameRevisionPart.size || '');
      setFieldValue('flashGap', sameRevisionPart.flashGap || '');
    }
  }, [isEditing, values.type, foundParts, sameRevisionPart, setFieldValue]);

  // debounce partId input for query to validate && auto fill
  useEffect(() => {
    if (isUsed) return undefined;
    const handler = setTimeout(() => {
      setDebouncedPartId(values.partId);
    }, 500);

    return () => clearTimeout(handler);
  }, [values.partId, isUsed]);

  // for material multiselect
  const hasOption = useCallback(
    (value) => materialOptions.some((o) => o.value === value),
    [materialOptions],
  );
  const createRow = useCallback((value) => ({ label: `Add "${value}"`, value, isNew: true }), []);
  const visibleOptions = useMemo(() => {
    if (values?.type !== PartTypes.PRODUCT) return materialOptions;
    if (!filteringText && !foundParts?.length) return materialOptions;
    // disable option if found in the found parts
    let options = [...materialOptions];
    if (foundParts?.length > 0) {
      // check if foundParts has material not in the list, add
      foundParts.forEach((p) => {
        if (p.material && !options.some((o) => o.value === p.material)) {
          options.push({ label: p.material, value: p.material });
        }
      });

      // check existing parts to disable used materials
      options = options.map((o) => {
        if (foundParts?.some((p) => p.material === o.value && p.revision === values.revision)) {
          return {
            ...o,
            disabled: true,
            disabledReason: 'A part with this material already exists for the same Part ID and Revision',
          };
        }
        return o;
      });
    }
    if (!filteringText) return options;

    const filtered = options.filter((o) => (o.label || '').toLowerCase().includes(filteringText.toLowerCase()));

    // If the typed text doesn't exist as an option, prepend a "Create" row
    if (filteringText.trim() && !hasOption(filteringText.trim())) {
      return [createRow(filteringText.trim()), ...filtered];
    }
    return filtered;
  }, [
    filteringText,
    foundParts,
    materialOptions,
    values?.type,
    values?.revision,
    hasOption,
    createRow,
  ]);
  const updateMaterialFields = (materialArr) => {
    setFieldValue('materialArr', materialArr);
    // set material for validation
    setFieldValue('material', materialArr.map((o) => o.value).join(', '));
  };

  return (
    <Modal
      size="large"
      onDismiss={onClose}
      visible={open}
      header={isEditing ? 'Edit a Part' : 'Add a Part'}
    >
      <form onSubmit={handleSubmit}>
        <SpaceBetween direction="vertical" size="s">
          <ColumnLayout columns={2}>
            <FormField
              label="Part Id"
              errorText={errors.partId}
            >
              <Input
                value={values.partId}
                onChange={({ detail }) => { setFieldValue('partId', detail.value); }}
                disabled={isUsed}
              />
            </FormField>
            <FormField
              label="Revision"
              errorText={errors.revision}
            >
              <Input
                value={values.revision}
                onChange={({ detail }) => { setFieldValue('revision', detail.value); }}
                disabled={isUsed}
              />
            </FormField>
            <FormField
              label="Type"
              errorText={errors.type}
            >
              <Autosuggest
                disabled={foundParts?.some((p) => p.partId === values.partId) || isUsed}
                value={values.type}
                onChange={({ detail }) => { setFieldValue('type', detail.value); }}
                options={Object.values(PartTypes).map((type) => ({ value: type }))}
                placeholder="Type or select a value"
                empty="No options found"
              />
            </FormField>
            <FormField
              label="Name"
              errorText={errors.name}
            >
              <Input
                value={values.name}
                onChange={({ detail }) => { setFieldValue('name', detail.value); }}
              />
            </FormField>
          </ColumnLayout>
          {InstancedPartTypes.includes(values.type) && (
            <FormField
              label="Instance"
              errorText={errors.instance}
            >
              <Input
                value={values.instance}
                disabled
              />
            </FormField>
          )}
          {values.type === PartTypes.TOOL && (
            <FormField
              label="Cavities"
            >
              <Container>
                <AttributeEditor
                  disableAddButton={
                    foundParts?.some((p) => p.partId === values.partId) || isUsed
                  }
                  gridLayout={[
                    {
                      rows: [[1]],
                      removeButton: { ownRow: false, width: 'auto' },
                    },
                  ]}
                  onAddButtonClick={() => setFieldValue('cavities', [...values.cavities, { name: String.fromCharCode(65 + values.cavities.length) }])}
                  onRemoveButtonClick={({
                    detail: { itemIndex },
                  }) => {
                    if (isUsed) return;
                    if (values.cavities.length === 1) return;
                    if (foundParts?.some((p) => p.partId === values.partId)) return;
                    const tmpItems = [...values.cavities];
                    tmpItems.splice(itemIndex, 1);
                    setFieldValue('cavities', tmpItems);
                  }}
                  removeButtonText="Remove"
                  items={values.cavities}
                  addButtonText="Add new cavity"
                  definition={[
                    {
                      label: 'Cavity Name',
                      control: (item, itemIndex) => (
                        <FormField
                          errorText={errors.cavities && errors.cavities[itemIndex]?.name}
                        >
                          <Input
                            value={item.name}
                            onChange={({ detail }) => setFieldValue(`cavities[${itemIndex}].name`, detail.value)}
                            placeholder="Enter cavity name"
                            disabled={
                              foundParts?.some((p) => p.partId === values.partId) || isUsed
                            }
                          />
                        </FormField>
                      ),
                    },
                  ]}
                  empty={<Box color="text-status-error">At least one cavity is required.</Box>}
                />
              </Container>
            </FormField>
          )}
          {values.type === PartTypes.PISTON && (
            <ColumnLayout columns={2}>
              <FormField label="Size" errorText={errors.size}>
                <Autosuggest
                  value={values.size}
                  onChange={({ detail }) => { setFieldValue('size', detail.value); }}
                  options={PistonSizeOptions}
                  placeholder="Type or select a size"
                  empty="No options found"
                  disabled={!!sameRevisionPart || isUsed}
                />
              </FormField>
              <FormField label="Flash Gap" errorText={errors.flashGap}>
                <Autosuggest
                  value={values.flashGap}
                  onChange={({ detail }) => { setFieldValue('flashGap', detail.value); }}
                  options={PistonFlashGapOptions}
                  placeholder="Type or select a flash gap"
                  empty="No options found"
                  disabled={!!sameRevisionPart || isUsed}
                />
              </FormField>
            </ColumnLayout>
          )}
          {values.type === PartTypes.PRODUCT && (
            <ColumnLayout columns={2}>
              <FormField
                label="Material"
                errorText={errors.material}
              >
                <Multiselect
                  selectedOptions={values.materialArr || []}
                  options={visibleOptions}
                  placeholder="Select or type to create…"
                  filteringType="manual"
                  keepOpen
                  empty="No options found"
                  disabled={isUsed}
                  onLoadItems={({ detail }) => {
                    // called while typing when filteringType="manual"
                    setFilteringText(detail.filteringText || '');
                  }}
                  onChange={({ detail }) => {
                    const picked = detail.selectedOptions || [];
                    const toCreate = picked.filter((o) => o.isNew);

                    if (toCreate.length > 0) {
                      // turn the pseudo "Create" rows into real options
                      const created = toCreate.map((o) => ({
                        label: o.value,
                        value: o.value,
                      }));

                      // add to master list (avoid dupes just in case)
                      const nextOptions = [
                        ...materialOptions,
                        ...created.filter((o) => !hasOption(o.value)),
                      ];

                      // new selection = existing (minus pseudo flags) + created
                      const nextSelected = [
                        ...picked.filter((o) => !o.isNew),
                        ...created,
                      ];

                      setMaterialOptions(nextOptions);
                      updateMaterialFields(nextSelected);
                      return;
                    }

                    updateMaterialFields(picked);
                  }}
                />
              </FormField>
              <FormField
                label="Step Type"
                errorText={errors.stepType}
              >
                <Select
                  disabled={foundParts?.some((p) => p.partId === values.partId) || isUsed}
                  expandToViewport
                  selectedOption={{ label: values.stepType, value: values.stepType }}
                  onChange={({ detail }) => {
                    setFieldValue('stepType', detail.selectedOption.value);
                  }}
                  options={stepOptions}
                />
              </FormField>
            </ColumnLayout>
          )}
          <FormField
            label="Link Parts"
            description="Reusable things needed to make this part — tools, pistons, fixtures. They survive the build and may live in another project."
            errorText={typeof errors.linkedParts === 'string'
              ? errors.linkedParts
              : errors.linkedParts && 'Every linked part needs a project and a part'}
            stretch
          >
            <Container>
              <PartRefEditor
                rows={values.linkedParts || []}
                onChange={(next) => setFieldValue('linkedParts', next)}
                // Internal parts link internal tooling; customer parts may
                // link from any project (null = no project filter).
                isInternal={isInternal || null}
                defaultProjectDocId={projectDocId}
                excludeTypes={[PartTypes.PRODUCT]}
                // Tool values on runs carry no project, so tools link within
                // the current project only; pistons/other may cross projects.
                sameProjectOnlyTypes={[PartTypes.TOOL]}
                // No self-links: a non-Product part being edited would
                // otherwise list itself.
                excludePPartDocId={initPart?.id}
                addButtonText="Link a part"
                empty="No linked parts."
              />
            </Container>
          </FormField>
          <FormField
            label="Components (BOM)"
            description="Consumables installed onto this part — parts used up per unit built, with per-unit quantities. Drives the lot install checklists."
            errorText={typeof errors.components === 'string'
              ? errors.components
              : errors.components
                  && 'Every component needs a project, a part, and a positive quantity'}
            stretch
          >
            <Container>
              <PartRefEditor
                rows={values.components || []}
                onChange={(next) => setFieldValue('components', next)}
                // BOM components are always internal parts.
                isInternal
                withQuantity
                // BOM lines are consumables — reusable tooling can't be a component.
                excludeTypes={[PartTypes.TOOL, PartTypes.PISTON]}
                addButtonText="Add component"
                empty="No components — this part has no BOM."
              />
            </Container>
          </FormField>
          <FormField
            label="Status"
            errorText={errors.status}
            stretch
          >
            <Select
              expandToViewport
              selectedOption={{ label: values.status, value: values.status }}
              onChange={({ detail }) => {
                setFieldValue('status', detail.selectedOption.value);
              }}
              options={Object.entries(PPartStatusMap).map(([key, vals]) => ({
                label: key,
                value: key,
                description: vals.description,
              }))}
            />
          </FormField>
          <FormField
            label="Notes"
            errorText={errors.notes}
            stretch
          >
            <Textarea
              value={values.notes}
              onChange={({ detail }) => { setFieldValue('notes', detail.value); }}
            />
          </FormField>

          <FormField label="Metadata" stretch>
            <Container>
              <AttributeEditor
                items={values.metadataArr}
                definition={[
                  {
                    label: 'Key',
                    control: (item, itemIndex) => (
                      <FormField errorText={errors.metadataArr?.[itemIndex]?.key}>
                        <Autosuggest
                          onChange={({ detail }) => {
                            setFieldValue(`metadataArr[${itemIndex}].key`, detail.value);
                            if (detail.value === 'Thickness Tolerance') {
                              setFieldValue(`metadataArr[${itemIndex}].value`, DefaultTolerance);
                            }
                          }}
                          value={item.key}
                          options={PPartMetadataKeyOptions[values.type] || []}
                          ariaLabel="Select or type a metadata key for the part"
                          placeholder="Enter or choose a metadata key"
                        />
                      </FormField>
                    ),
                  },
                  {
                    label: 'Value',
                    control: (item, itemIndex) => (
                      <FormField errorText={errors.metadataArr?.[itemIndex]?.value}>
                        <Input
                          value={item.value}
                          onChange={({ detail }) => setFieldValue(`metadataArr[${itemIndex}].value`, detail.value)}
                          placeholder="Enter metadata value"
                        />
                      </FormField>
                    ),
                  },
                ]}
                addButtonText="Add metadata"
                removeButtonText="&times;"
                onAddButtonClick={() => {
                  const newMetadata = [...(values.metadataArr || []), { key: '', value: '' }];
                  setFieldValue('metadataArr', newMetadata);
                }}
                onRemoveButtonClick={({ detail: { itemIndex } }) => {
                  const newMetadata = [...(values.metadataArr || [])];
                  newMetadata.splice(itemIndex, 1);
                  setFieldValue('metadataArr', newMetadata);
                }}
                empty="No metadata found"
                gridLayout={[
                  {
                    rows: [[1, 1]],
                    removeButton: { ownRow: false, width: 'auto' },
                  },
                ]}
              />
            </Container>
          </FormField>

          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" formAction="none" onClick={onClose}>Cancel</Button>
              <Button variant="primary" type="submit" loading={isSubmitting}>{isEditing ? 'Update' : 'Add'}</Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </form>
    </Modal>
  );
}

AddPartModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  pParts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    partId: PropTypes.string.isRequired,
  })).isRequired,
  projectDocId: PropTypes.string.isRequired,
  isInternal: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  isUsed: PropTypes.bool,
  initPart: PropTypes.shape({
    partId: PropTypes.string,
    revision: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    notes: PropTypes.string,
  }),
};
